import {Injectable, OnDestroy} from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import {Observable, Subscription, throwError} from "rxjs";
import {catchError, finalize, shareReplay, switchMap} from "rxjs/operators";
import {AuthService} from "../../modules/auth/services/auth.service";
import {User} from "src/app/modules/auth/models/User";
import {Router} from "@angular/router";

@Injectable()
export class AuthInterceptor implements HttpInterceptor, OnDestroy {
  public user?: User | null;
  private refreshRequest?: Observable<User>;
  private refreshingToken?: string;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router
  ) {
    const userSubscription = this.authService.user$.subscribe(
      (user: User | null) => {
        if (user) {
          this.user = user;
        } else {
          this.user = null;
        }
      }
    );
    this.subscriptions.push(userSubscription);
  }

  intercept(
    request: HttpRequest<User>,
    next: HttpHandler
  ): Observable<HttpEvent<User>> {
    if (
      request.method === "GET" &&
      /\/public\/works\/[^/]+\/[^/?]+(?:\?|$)/.test(request.url)
    )
      return next.handle(request);
    const requestUserId = this.user?._id;
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const refreshToken = this.user?.tokens?.refreshToken;
        if (
          error.status !== 401 ||
          this.isRefreshTokenRequest(request) ||
          requestUserId !== this.user?._id
        ) {
          return throwError(() => error);
        }
        if (refreshToken) {
          return this.refreshSession(refreshToken).pipe(
            switchMap((user) => this.retryRequest(request, next, user))
          );
        }
        this.authService.logout();
        this.navigateToLogin();
        return throwError(() => error);
      }),
      catchError((error: HttpErrorResponse) => this.propagateError(error))
    );
  }

  private refreshSession(refreshToken: string): Observable<User> {
    if (!this.refreshRequest || this.refreshingToken !== refreshToken) {
      // Share both success and failure: concurrent callers must never wait forever.
      const refreshRequest = this.authService.refreshToken(refreshToken).pipe(
        catchError((error: HttpErrorResponse) => {
          if (
            (error.status === 401 || error.status === 403) &&
            !this.isLegalRequiredError(error) &&
            this.user?.tokens?.refreshToken === refreshToken
          ) {
            this.authService.logout();
            this.navigateToLogin();
          }
          return throwError(() => error);
        }),
        finalize(() => {
          if (this.refreshRequest === refreshRequest) {
            this.refreshRequest = undefined;
            this.refreshingToken = undefined;
          }
        }),
        shareReplay({bufferSize: 1, refCount: true})
      );
      this.refreshingToken = refreshToken;
      this.refreshRequest = refreshRequest;
    }
    return this.refreshRequest;
  }

  private retryRequest(
    request: HttpRequest<User>,
    next: HttpHandler,
    user: User
  ): Observable<HttpEvent<User>> {
    return next.handle(this.addTokenHeader(request, user)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 401 &&
          this.user?.tokens?.accessToken === user.tokens?.accessToken
        ) {
          this.authService.logout();
          this.navigateToLogin();
        }
        return throwError(() => error);
      })
    );
  }

  private propagateError(error: HttpErrorResponse): Observable<never> {
    if (this.isLegalRequiredError(error)) this.redirectToLegalCompletion();
    const payload: unknown = error.error;
    const code =
      payload && typeof payload === "object" && "error" in payload
        ? payload.error
        : payload;
    if (
      error.status === 429 &&
      (typeof payload === "string" ||
        code === "Too many requests, please retry later." ||
        code === "Too many requests from this IP, please retry later.")
    ) {
      return throwError(
        () =>
          new HttpErrorResponse({
            error: {error: "REQUEST_RATE_LIMITED"},
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
          })
      );
    }
    return throwError(() => error);
  }

  private isLegalRequiredError(error: HttpErrorResponse): boolean {
    return error.status === 403 && error.error?.error === "LEGAL_REQUIRED";
  }

  private isRefreshTokenRequest(request: HttpRequest<User>): boolean {
    return /\/refresh_token\/?(?:[?#]|$)/i.test(request.url);
  }

  private redirectToLegalCompletion(): void {
    if (this.router.url.startsWith("/complete-signup")) {
      return;
    }

    this.router
      .navigate(["/complete-signup"], {
        queryParams: {redirectTo: this.router.url},
      })
      .catch(() => undefined);
  }

  private navigateToLogin(): void {
    if (
      this.router.url.startsWith("/email-change") ||
      this.router.url.startsWith("/work/")
    )
      return;
    this.router.navigate(["/login"]).catch(() => undefined);
  }

  private addTokenHeader(request: HttpRequest<User>, user: User) {
    if (user?.tokens) {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${user.tokens.accessToken}`,
      };

      return request.clone({
        setHeaders: headers,
      });
    }
    return request;
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
